import { zodResolver } from "@hookform/resolvers/zod";
import { TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import LockFillIcon from "@/assets/icons/lock-fill-icon.svg?react";
import OneIdIconWhite from "@/assets/icons/oneid-icon-white.svg?react";
import OneIdIcon from "@/assets/icons/oneid-icon.svg?react";
import logo from "@/assets/yia_logo.svg";
import { useAuthLogin } from "@/auth/hooks/useAuthLogin";
import { useAuthWithOneId } from "@/auth/hooks/useAuthWithOneId";
import { getLoginSchema, LoginSchema } from "@/auth/schemas/loginSchema";
import { isLoginFailure, LoginFailure } from "@/auth/types/LoginAuth";
import { Form } from "@/components/_form/Form";
import { TextField } from "@/components/_rhf/Text";
import { Button } from "@/components/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/Collapsible";
import { LoadingButton } from "@/components/LoadingButton/LoadingButton";
import { PageLoading } from "@/components/PageLoading";
import { useRouter } from "@/hooks/useRouter";
import { cn } from "@/lib/utils";
import { paths } from "@/routes/paths";

const BLOCKED_LOGIN_SESSION_KEY = "auth:blocked-login-state";

const getBlockedLoginStateFromSession = (): LoginFailure | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = sessionStorage.getItem(BLOCKED_LOGIN_SESSION_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<LoginFailure>;
    if (parsed.status !== "blocked" || typeof parsed.detail !== "string") {
      return null;
    }

    return {
      status: "blocked",
      detail: parsed.detail,
    };
  } catch {
    return null;
  }
};

const persistBlockedLoginState = (failure: LoginFailure): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (failure.status !== "blocked") {
    sessionStorage.removeItem(BLOCKED_LOGIN_SESSION_KEY);
    return;
  }

  sessionStorage.setItem(
    BLOCKED_LOGIN_SESSION_KEY,
    JSON.stringify({
      status: "blocked",
      detail: failure.detail,
    }),
  );
};

export const AuthLogin = () => {
  const { t } = useTranslation("login");
  const { t: tOneId } = useTranslation("one-id");
  const [loading, setLoading] = useState<boolean>(false);
  const [oneIdLoading, setOneIdLoading] = useState<boolean>(false);
  const [oneIdError, setOneIdError] = useState<string | null>(null);
  const [credentialFailure, setCredentialFailure] =
    useState<LoginFailure | null>(() => getBlockedLoginStateFromSession());
  const [credentialFormOpen, setCredentialFormOpen] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const processedCodeRef = useRef<string | null>(null);
  const authLogin = useAuthLogin();
  const authWithOneId = useAuthWithOneId();
  const authWithOneIdRef = useRef(authWithOneId);
  const router = useRouter();
  const oneIdDeeplink = import.meta.env.VITE_ONEID_DEEPLINK || "";
  const supportContactUrl = import.meta.env.VITE_SUPPORT_CONTACT_URL || "";

  // hCaptcha is temporarily disabled.
  // const hcaptchaSiteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY?.trim() || "";

  authWithOneIdRef.current = authWithOneId;

  const form = useForm<LoginSchema>({
    resolver: zodResolver(getLoginSchema()),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Redirect if user already has access_token
  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      const returnTo = searchParams.get("returnTo") || paths.dashboard.root;
      router.replace(returnTo);
    }
  }, [router, searchParams]);

  useEffect(() => {
    if (code && code !== processedCodeRef.current) {
      processedCodeRef.current = code;
      setOneIdLoading(true);
      setOneIdError(null);
      authWithOneIdRef.current(code).catch(() => {
        setOneIdError(tOneId("errors.authenticationFailed"));
        setOneIdLoading(false);
      });
    }
  }, [code, tOneId]);

  const onSubmit = async (data: LoginSchema) => {
    try {
      setLoading(true);
      setCredentialFailure(null);
      sessionStorage.removeItem(BLOCKED_LOGIN_SESSION_KEY);
      await authLogin(data.username, data.password);
      sessionStorage.removeItem(BLOCKED_LOGIN_SESSION_KEY);
    } catch (error) {
      if (isLoginFailure(error)) {
        setCredentialFailure(error);
        persistBlockedLoginState(error);
        return;
      }

      const genericFailure: LoginFailure = {
        status: "error",
        detail: t("authErrors.generic"),
      };
      setCredentialFailure(genericFailure);
      persistBlockedLoginState(genericFailure);
    } finally {
      setLoading(false);
    }
  };

  const handleOneIdLogin = () => {
    if (oneIdDeeplink) {
      window.location.href = oneIdDeeplink;
      return;
    }
    setOneIdError(tOneId("errors.deeplinkNotConfigured"));
  };

  const handleSupportContact = () => {
    if (!supportContactUrl) {
      return;
    }

    window.open(supportContactUrl, "_blank", "noopener,noreferrer");
  };

  const isProcessingOneIdCode =
    Boolean(code) &&
    !oneIdError &&
    (oneIdLoading || processedCodeRef.current !== code);
  const isCredentialBlocked = credentialFailure?.status === "blocked";
  const isCredentialLoginDisabled =
    loading || oneIdLoading || isCredentialBlocked;

  if (isProcessingOneIdCode) {
    return <PageLoading />;
  }

  return (
    <div
      className={cn(
        "flex w-full max-w-[510px] flex-col rounded-2xl bg-white p-5 shadow-lg sm:p-6",
        !isCredentialBlocked && "border border-border-secondary",
      )}
    >
      {!isCredentialBlocked && (
        <div className="mb-5 flex flex-col items-center gap-3 text-center">
          <img
            src={logo}
            alt="YIA Logo"
            className="h-11 w-auto object-contain"
          />
          <h2
            className="font-base px-2 pt-5 text-3xl leading-tight text-text-primary sm:text-[42px]"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            <span className="text-primary">{t("title.highlight")}</span>{" "}
            {t("title.rest")}
          </h2>
          <p className="max-w-[360px] text-base text-text-secondary">
            {t("description")}
          </p>
        </div>
      )}

      {isCredentialBlocked ? (
        <div className="rounded-xl border border-border-secondary p-3 sm:p-4">
          <div className="rounded-lg bg-white p-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#D5343B] shadow-[inset_0_-4px_8px_rgba(255,255,255,0.35)]">
              <LockFillIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-2xl font-semibold text-text-primary">
              {t("authErrors.blockedTitle")}
            </h3>
            <p className="mt-2 text-sm font-normal text-text-secondary sm:px-8">
              {credentialFailure?.detail || t("authErrors.blockedDescription")}
            </p>
            <Button
              type="button"
              variant="primary"
              className="mt-5 w-full"
              onClick={handleSupportContact}
            >
              {t("authErrors.contactSupportButton")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {oneIdError && (
            <div className="mb-3 rounded-md bg-bg-error-primary p-3 text-sm text-text-error-primary">
              {oneIdError}
            </div>
          )}

          <Button
            type="button"
            variant={credentialFormOpen ? "outline" : "primary"}
            className={
              credentialFormOpen
                ? "w-full bg-white hover:bg-bg-secondary-hover"
                : "w-full"
            }
            onClick={handleOneIdLogin}
            disabled={loading || oneIdLoading}
          >
            {credentialFormOpen ? (
              <OneIdIcon className="mr-2 h-5 w-5" />
            ) : (
              <OneIdIconWhite className="mr-2 h-5 w-5" />
            )}
            {t("loginWithOneId")}
          </Button>

          <Collapsible
            open={credentialFormOpen}
            onOpenChange={setCredentialFormOpen}
          >
            <div className="my-4 flex w-full items-center justify-center">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {t("orText")}
                </button>
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent>
              <div className="rounded-xl border border-border-secondary p-3 sm:p-4">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex w-full flex-col gap-4"
                  >
                    <TextField
                      name="username"
                      label={t("usernameLabel")}
                      placeholder={usernameFocused ? "" : "admin"}
                      required
                      autoComplete="username"
                      onFocus={() => setUsernameFocused(true)}
                      onBlur={() => setUsernameFocused(false)}
                    />
                    <TextField
                      name="password"
                      label={t("passwordLabel")}
                      placeholder={passwordFocused ? "" : "••••••••"}
                      type="password"
                      required
                      autoComplete="current-password"
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                    />
                    {credentialFailure && (
                      <div className="-mt-2 flex items-start gap-2 text-sm text-text-error-primary">
                        {credentialFailure.status === "warning" ? (
                          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                        ) : (
                          <X className="mt-0.5 h-4 w-4 shrink-0" />
                        )}
                        <p>{credentialFailure.detail}</p>
                      </div>
                    )}
                    {/* hCaptcha UI is temporarily disabled. */}
                    <LoadingButton
                      type="submit"
                      isLoading={loading}
                      variant="primary"
                      size="lg"
                      className="flex w-full items-center justify-center text-md"
                      disabled={isCredentialLoginDisabled}
                    >
                      {t("loginButton")}
                    </LoadingButton>
                  </form>
                </Form>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  );
};
